/**
 * Category-page slug → config mapping.
 *
 * The landing's "Shop by category" tiles (and the desktop BROWSE sidebar) route
 * to `/category/<slug>`, where `<slug>` is the BROWSE category id
 * (`components/feed/browse-categories.ts::BROWSE_CATEGORIES`). This module is
 * the single source of truth for what each slug renders:
 *
 *  - `lego` / `trading-cards` are backed by the feed's `listingCategory` axis
 *    ("lego" == building blocks, "trading_card" == TCG) — the exact same axis
 *    the feed's filter chips drive, so the category page reuses the feed's
 *    filtering verbatim, just with the category *locked*.
 *  - `sneakers` / `figures` / `watches` / `comics` have no inventory yet, so
 *    they render a "coming soon" page with a launch countdown (see
 *    `CATEGORY_LAUNCH_AT`). TODO(v3): promote to `{ kind: "listings" }` here
 *    once their taxonomy/inventory exists — the route needs no other change.
 *
 * Pure + client-safe (no icons, no server imports) so both the server route
 * (slug validation / metadata) and the client page can import it.
 */
import type { ListingCategory } from "@kraahl/contract";

/** The feed's locked category axis — the canonical `ListingCategory` from `@kraahl/contract`. */
export type CategoryListingAxis = ListingCategory;

/** What a category slug resolves to. Discriminated on `kind`. */
export type CategoryConfig =
  | { kind: "listings"; listingCategory: CategoryListingAxis }
  | { kind: "coming-soon" };

/**
 * Launch instant for the "coming soon" categories — Sunday 16 August 2026,
 * 23:59, in the visitor's LOCAL timezone (month is 0-indexed → 7 = August).
 * PLACEHOLDER date for the countdown — set this to the real launch when known.
 * Single source of truth for the countdown target.
 */
export const CATEGORY_LAUNCH_AT = new Date(2026, 7, 16, 23, 59, 0);

const CATEGORY_CONFIGS: Record<string, CategoryConfig> = {
  lego: { kind: "listings", listingCategory: "lego" },
  "trading-cards": { kind: "listings", listingCategory: "trading_card" },
  sneakers: { kind: "coming-soon" },
  figures: { kind: "coming-soon" },
  watches: { kind: "coming-soon" },
  comics: { kind: "listings", listingCategory: "comic" },
};

/**
 * Inverse of the `listings`-kind entries above — feed axis value → BROWSE
 * slug (= `/categories/<slug>.webp` photo id). Exhaustive Record: a new
 * category fails compile here until it declares its slug (ICONS.md
 * § Category iconography consumers rely on this for cutout lookups).
 */
export const CATEGORY_AXIS_TO_SLUG: Record<CategoryListingAxis, string> = {
  lego: "lego",
  trading_card: "trading-cards",
  comic: "comics",
};

/** Resolve a category slug to its config, or `null` if the slug is unknown. */
export function getCategoryConfig(slug: string): CategoryConfig | null {
  return CATEGORY_CONFIGS[slug] ?? null;
}

/** Whether a slug maps to a real category page (used for route 404s). */
export function isKnownCategorySlug(slug: string): boolean {
  return Object.prototype.hasOwnProperty.call(CATEGORY_CONFIGS, slug);
}
